/** Disabled controls say why (Curate slice 5).
 * Disabled buttons get no hover or focus (`Button` has `disabled:pointer-events-none`), so a
 * `title` on the button itself never shows. `WhyDisabled` puts the reason on a focusable
 * wrapper instead: a tooltip on hover and keyboard focus, read out by screen readers.
 * Menus and dialogs show the reason as visible text instead (`ReasonLine`). */
import { useId, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function WhyDisabled({ reason, children, className }: {
  /** null/undefined: the control is enabled (or self-evident) and no tooltip shows. */
  reason?: string | null;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  // The wrapper is always rendered so the child doesn't remount when the reason comes and goes.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn("inline-flex", className)}
            tabIndex={reason ? 0 : undefined}
            aria-describedby={reason ? id : undefined}
          >
            {children}
            {reason && <span id={id} className="sr-only">{reason}</span>}
          </span>
        </TooltipTrigger>
        {reason && <TooltipContent className="max-w-xs">{reason}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
}

/** A muted reason line beside or under a disabled control; link it with `aria-describedby={id}`. */
export function ReasonLine({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return <p id={id} className={cn("text-xs text-muted-foreground", className)}>{children}</p>;
}
